import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { getBlockColor, TETROMINO_COLORS } from '../materials/SpectrumMaterial';

// Shared geometries - created once, reused
const sharedBoxGeometry = new THREE.BoxGeometry(1, 1, 0.6);
const sharedRingGeometry = new THREE.RingGeometry(0.8, 1, 32);
const sharedCircleGeometry = new THREE.CircleGeometry(0.5, 32);
const sharedSphereGeometry = new THREE.SphereGeometry(0.15, 8, 8);
const sharedGlowCircleGeometry = new THREE.CircleGeometry(0.4, 12);
const sharedPlaneGeometry = new THREE.PlaneGeometry(11, 1.2);
const sharedStreakGeometry = new THREE.PlaneGeometry(1, 0.15);
const sharedTrailGeometry = new THREE.PlaneGeometry(0.3, 1);

// Temp objects for matrix calculations
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempScale = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempEuler = new THREE.Euler();

const TetrisFinisher = ({ onComplete, blocks = [] }) => {
  const groupRef = useRef();
  const startTime = useRef(null);

  // Refs for instanced meshes
  const shatteredInstanceRef = useRef();
  const trailInstanceRef = useRef();
  const glowParticleInstanceRef = useRef();
  const glowHaloInstanceRef = useRef();
  const streakInstanceRef = useRef();
  const flashInstanceRef = useRef();

  // Single meshes refs
  const ringRef = useRef();
  const burstRef = useRef();

  // Get all tetromino colors
  const allColors = useMemo(() => Object.values(TETROMINO_COLORS), []);

  // Convert blocks to shattered pieces data
  const shatteredPieces = useMemo(() => {
    const pieces = [];

    const sourceBlocks = blocks.length > 0 ? blocks : (() => {
      const fallback = [];
      for (let row = 16; row < 20; row++) {
        for (let col = 0; col < 10; col++) {
          fallback.push({
            row,
            col,
            colorClass: allColors[Math.floor(Math.random() * allColors.length)]
          });
        }
      }
      return fallback;
    })();

    sourceBlocks.forEach((block) => {
      const baseX = block.col - 4.5;
      const baseY = 20 - block.row - 0.5;
      const color = typeof block.colorClass === 'string'
        ? getBlockColor(block.colorClass)
        : block.colorClass;

      const pieceCount = 3 + Math.floor(Math.random() * 2);

      for (let p = 0; p < pieceCount; p++) {
        const offsetX = (Math.random() - 0.5) * 0.4;
        const offsetY = (Math.random() - 0.5) * 0.4;

        const angle = Math.random() * Math.PI * 2;
        const speed = 8 + Math.random() * 12;
        const upwardBias = 15 + Math.random() * 10;

        pieces.push({
          startPosition: [baseX + offsetX, baseY + offsetY, 0.3],
          color: new THREE.Color(color),
          velocity: [
            Math.cos(angle) * speed + (baseX * 1.5),
            upwardBias + Math.random() * 8,
            Math.sin(angle) * speed * 0.3
          ],
          rotationSpeed: [
            (Math.random() - 0.5) * 15,
            (Math.random() - 0.5) * 15,
            (Math.random() - 0.5) * 15
          ],
          size: 0.2 + Math.random() * 0.25,
          delay: (block.col / 10) * 0.08 + Math.random() * 0.05,
          rotation: [0, 0, 0],
        });
      }
    });

    return pieces;
  }, [blocks, allColors]);

  // Get unique rows for effects
  const uniqueRows = useMemo(() => {
    if (blocks.length === 0) return [16, 17, 18, 19];
    const rows = [...new Set(blocks.map(b => b.row))];
    return rows.sort((a, b) => a - b);
  }, [blocks]);

  // Rainbow streaks data
  const rainbowStreaks = useMemo(() => {
    const streaks = [];
    uniqueRows.forEach((row, i) => {
      const y = 20 - row - 0.5;
      streaks.push({ startY: y, delay: i * 0.05, direction: 1, color: new THREE.Color(allColors[i % allColors.length]) });
      streaks.push({ startY: y, delay: i * 0.05 + 0.03, direction: -1, color: new THREE.Color(allColors[(i + 1) % allColors.length]) });
    });
    return streaks;
  }, [uniqueRows, allColors]);

  // Glow particles data
  const glowParticles = useMemo(() => {
    const particles = [];
    const count = 50;

    for (let i = 0; i < count; i++) {
      const row = uniqueRows[Math.floor(Math.random() * uniqueRows.length)];
      const x = (Math.random() - 0.5) * 10;
      const y = 20 - row - 0.5 + (Math.random() - 0.5);

      const angle = Math.random() * Math.PI * 2;
      const speed = 5 + Math.random() * 8;

      particles.push({
        position: [x, y, 0.2],
        color: new THREE.Color(Math.random() > 0.3 ? '#FFFFFF' : allColors[Math.floor(Math.random() * allColors.length)]),
        delay: Math.random() * 0.15,
        velocity: [Math.cos(angle) * speed, 8 + Math.random() * 12, (Math.random() - 0.5) * 3],
      });
    }

    return particles;
  }, [uniqueRows, allColors]);

  // Row flash data
  const rowFlashes = useMemo(() => {
    return uniqueRows.map((row, i) => ({
      y: 20 - row - 0.5,
      delay: i * 0.02,
    }));
  }, [uniqueRows]);

  // Materials
  const shatteredMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 1.5,
    metalness: 0.4,
    roughness: 0.1,
    transparent: true,
    opacity: 1,
  }), []);

  const trailMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const glowMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const flashMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  const streakMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  // Initialize instanced meshes
  useEffect(() => {
    // Initialize shattered pieces
    if (shatteredInstanceRef.current) {
      shatteredPieces.forEach((piece, i) => {
        tempPosition.set(...piece.startPosition);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        shatteredInstanceRef.current.setMatrixAt(i, tempMatrix);
        shatteredInstanceRef.current.setColorAt(i, piece.color);
      });
      shatteredInstanceRef.current.instanceMatrix.needsUpdate = true;
      shatteredInstanceRef.current.instanceColor.needsUpdate = true;
    }

    // Initialize trails (same count as shattered pieces)
    if (trailInstanceRef.current) {
      shatteredPieces.forEach((_, i) => {
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        trailInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      trailInstanceRef.current.instanceMatrix.needsUpdate = true;
    }

    // Initialize glow particles
    if (glowParticleInstanceRef.current) {
      glowParticles.forEach((particle, i) => {
        tempPosition.set(...particle.position);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        glowParticleInstanceRef.current.setMatrixAt(i, tempMatrix);
        glowParticleInstanceRef.current.setColorAt(i, particle.color);
      });
      glowParticleInstanceRef.current.instanceMatrix.needsUpdate = true;
      glowParticleInstanceRef.current.instanceColor.needsUpdate = true;
    }

    // Initialize glow halos
    if (glowHaloInstanceRef.current) {
      glowParticles.forEach((particle, i) => {
        tempPosition.set(...particle.position);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        glowHaloInstanceRef.current.setMatrixAt(i, tempMatrix);
        glowHaloInstanceRef.current.setColorAt(i, particle.color);
      });
      glowHaloInstanceRef.current.instanceMatrix.needsUpdate = true;
      glowHaloInstanceRef.current.instanceColor.needsUpdate = true;
    }

    // Initialize streaks
    if (streakInstanceRef.current) {
      rainbowStreaks.forEach((streak, i) => {
        tempPosition.set(0, streak.startY, 0.3);
        tempScale.setScalar(0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        streakInstanceRef.current.setMatrixAt(i, tempMatrix);
        streakInstanceRef.current.setColorAt(i, streak.color);
      });
      streakInstanceRef.current.instanceMatrix.needsUpdate = true;
      streakInstanceRef.current.instanceColor.needsUpdate = true;
    }

    // Initialize row flashes
    if (flashInstanceRef.current) {
      rowFlashes.forEach((flash, i) => {
        tempPosition.set(0, flash.y, 0.25);
        tempScale.set(1, 1, 1);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        flashInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      flashInstanceRef.current.instanceMatrix.needsUpdate = true;
      flashInstanceRef.current.visible = false;
    }
  }, [shatteredPieces, glowParticles, rainbowStreaks, rowFlashes]);

  // Single consolidated useFrame for ALL animations
  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    if (elapsed > 2.0) {
      onComplete?.();
      return;
    }

    const gravity = -35;

    // Animate shattered pieces
    if (shatteredInstanceRef.current) {
      shatteredPieces.forEach((piece, i) => {
        const adjustedTime = elapsed - piece.delay;

        if (adjustedTime <= 0 || adjustedTime > 1.8) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          shatteredInstanceRef.current.setMatrixAt(i, tempMatrix);
          if (trailInstanceRef.current) {
            trailInstanceRef.current.setMatrixAt(i, tempMatrix);
          }
          return;
        }

        const t = adjustedTime;
        const x = piece.startPosition[0] + piece.velocity[0] * t;
        const y = piece.startPosition[1] + piece.velocity[1] * t + 0.5 * gravity * t * t;
        const z = piece.startPosition[2] + piece.velocity[2] * t;

        // Update rotation
        piece.rotation[0] += piece.rotationSpeed[0] * 0.016;
        piece.rotation[1] += piece.rotationSpeed[1] * 0.016;
        piece.rotation[2] += piece.rotationSpeed[2] * 0.016;

        tempPosition.set(x, y, z);
        tempEuler.set(piece.rotation[0], piece.rotation[1], piece.rotation[2]);
        tempQuaternion.setFromEuler(tempEuler);
        tempScale.setScalar(piece.size);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        shatteredInstanceRef.current.setMatrixAt(i, tempMatrix);

        // Trail
        if (trailInstanceRef.current && adjustedTime < 0.8) {
          tempPosition.set(x - piece.velocity[0] * 0.05, y - piece.velocity[1] * 0.05, z - 0.1);
          tempScale.set(1 + adjustedTime * 2, piece.size * 0.3, 1);
          tempQuaternion.identity();
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          trailInstanceRef.current.setMatrixAt(i, tempMatrix);
        } else if (trailInstanceRef.current) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          trailInstanceRef.current.setMatrixAt(i, tempMatrix);
        }
      });
      shatteredInstanceRef.current.instanceMatrix.needsUpdate = true;
      if (trailInstanceRef.current) {
        trailInstanceRef.current.instanceMatrix.needsUpdate = true;
        trailInstanceRef.current.material.opacity = Math.max(0, 0.6 * (1 - elapsed / 0.8));
      }
    }

    // Animate glow particles
    if (glowParticleInstanceRef.current) {
      const particleGravity = -15;
      glowParticles.forEach((particle, i) => {
        const adjustedTime = elapsed - particle.delay;

        if (adjustedTime <= 0 || adjustedTime > 1.2) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          glowParticleInstanceRef.current.setMatrixAt(i, tempMatrix);
          if (glowHaloInstanceRef.current) {
            glowHaloInstanceRef.current.setMatrixAt(i, tempMatrix);
          }
          return;
        }

        const progress = adjustedTime / 1.2;
        const x = particle.position[0] + particle.velocity[0] * adjustedTime;
        const y = particle.position[1] + particle.velocity[1] * adjustedTime + 0.5 * particleGravity * adjustedTime * adjustedTime;
        const z = particle.position[2] + particle.velocity[2] * adjustedTime;

        const pulse = Math.sin(adjustedTime * 15) * 0.3 + 0.7;
        const fade = 1 - progress;
        const scale = 0.15 * pulse * fade + 0.05;

        tempPosition.set(x, y, z);
        tempScale.setScalar(scale);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        glowParticleInstanceRef.current.setMatrixAt(i, tempMatrix);

        // Halo
        if (glowHaloInstanceRef.current) {
          tempPosition.set(x, y, z - 0.1);
          tempScale.setScalar(0.4 * fade);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          glowHaloInstanceRef.current.setMatrixAt(i, tempMatrix);
        }
      });
      glowParticleInstanceRef.current.instanceMatrix.needsUpdate = true;
      if (glowHaloInstanceRef.current) {
        glowHaloInstanceRef.current.instanceMatrix.needsUpdate = true;
      }
    }

    // Animate rainbow streaks
    if (streakInstanceRef.current) {
      rainbowStreaks.forEach((streak, i) => {
        const adjustedTime = elapsed - streak.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.6) {
          tempScale.setScalar(0);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          streakInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        const progress = adjustedTime / 0.6;
        const x = streak.direction * (-6 + progress * 12);
        const scaleX = 2 + Math.sin(progress * Math.PI) * 3;

        tempPosition.set(x, streak.startY, 0.3);
        tempScale.set(scaleX, 1, 1);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        streakInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      streakInstanceRef.current.instanceMatrix.needsUpdate = true;
      streakInstanceRef.current.material.opacity = Math.sin((elapsed / 0.6) * Math.PI) * 0.9;
    }

    // Animate row flashes
    if (flashInstanceRef.current) {
      let anyVisible = false;
      rowFlashes.forEach((flash, i) => {
        const adjustedTime = elapsed - flash.delay;

        if (adjustedTime <= 0 || adjustedTime > 0.4) {
          tempScale.set(1, 0, 1);
          tempPosition.set(0, flash.y, 0.25);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          flashInstanceRef.current.setMatrixAt(i, tempMatrix);
          return;
        }

        anyVisible = true;
        const progress = adjustedTime / 0.4;
        const scaleY = 1 + Math.sin(progress * Math.PI) * 0.3;

        tempPosition.set(0, flash.y, 0.25);
        tempScale.set(1, scaleY, 1);
        tempQuaternion.identity();
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        flashInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      flashInstanceRef.current.visible = anyVisible;
      flashInstanceRef.current.instanceMatrix.needsUpdate = true;
      flashInstanceRef.current.material.opacity = Math.sin((elapsed / 0.4) * Math.PI) * 0.95;
    }

    // Animate explosion ring and burst
    if (ringRef.current) {
      if (elapsed < 0.5) {
        ringRef.current.visible = true;
        const scale = elapsed * 40;
        ringRef.current.scale.setScalar(scale);
        ringRef.current.material.opacity = 0.8 * (1 - elapsed / 0.5);
      } else {
        ringRef.current.visible = false;
      }
    }

    if (burstRef.current) {
      if (elapsed < 0.3) {
        burstRef.current.visible = true;
        const scale = 1 + elapsed * 15;
        burstRef.current.scale.setScalar(scale);
        burstRef.current.material.opacity = 1 - elapsed / 0.3;
      } else {
        burstRef.current.visible = false;
      }
    }
  });

  return (
    <group ref={groupRef}>
      {/* Central explosion - single meshes (only 2) */}
      <mesh ref={ringRef} position={[0, 10, 0.5]} visible={false} geometry={sharedRingGeometry}>
        <meshBasicMaterial
          color={0xffffff}
          transparent
          opacity={0.8}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh ref={burstRef} position={[0, 10, 0.4]} visible={false} geometry={sharedCircleGeometry}>
        <meshBasicMaterial
          color={0xffffff}
          transparent
          opacity={1}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Row flashes - Instanced */}
      {rowFlashes.length > 0 && (
        <instancedMesh
          ref={flashInstanceRef}
          args={[sharedPlaneGeometry, flashMaterial, rowFlashes.length]}
          frustumCulled={false}
        />
      )}

      {/* Rainbow streaks - Instanced */}
      {rainbowStreaks.length > 0 && (
        <instancedMesh
          ref={streakInstanceRef}
          args={[sharedStreakGeometry, streakMaterial, rainbowStreaks.length]}
          frustumCulled={false}
        />
      )}

      {/* Shattered pieces - Instanced */}
      {shatteredPieces.length > 0 && (
        <instancedMesh
          ref={shatteredInstanceRef}
          args={[sharedBoxGeometry, shatteredMaterial, shatteredPieces.length]}
          frustumCulled={false}
        />
      )}

      {/* Trails for shattered pieces - Instanced */}
      {shatteredPieces.length > 0 && (
        <instancedMesh
          ref={trailInstanceRef}
          args={[sharedTrailGeometry, trailMaterial, shatteredPieces.length]}
          frustumCulled={false}
        />
      )}

      {/* Glow particles - Instanced */}
      {glowParticles.length > 0 && (
        <instancedMesh
          ref={glowParticleInstanceRef}
          args={[sharedSphereGeometry, glowMaterial, glowParticles.length]}
          frustumCulled={false}
        />
      )}

      {/* Glow halos - Instanced */}
      {glowParticles.length > 0 && (
        <instancedMesh
          ref={glowHaloInstanceRef}
          args={[sharedGlowCircleGeometry, glowMaterial, glowParticles.length]}
          frustumCulled={false}
        />
      )}
    </group>
  );
};

export default TetrisFinisher;
